// Resolves a drag-drawn connection between two TOC navigator nodes into the
// single Firestore write that stores it. The board draws links left→right
// (doelgroep → activiteit → mechanisme → effect); only those three adjacent
// hops are valid. The caller applies FieldValue.arrayUnion (link) or
// arrayRemove (unlink) to the returned {collection, docid, field} using valueId.
//
// Link storage mirrors the rest of the TOC (see tocNavigatorChain.js) — arrays
// hold entity `ID`s (not docids), and the array does not always live on the
// downstream node:
//   doel → act:  Activities.Targetgroup[]   (owner = act,  value = doel.id)
//   act  → mech: TocMechanisms.Activity[]   (owner = mech, value = act.id)
//   mech → eff:  TocMechanisms.Effect[]     (owner = mech, value = eff.id)
// AI-CONTEXT: See docs/features/toc-drag-drop-links.md

/**
 * @typedef {object} NavNode
 * @property {string} id - Entity ID (the link value).
 * @property {'doel'|'act'|'mech'|'eff'} step - Chain column.
 * @property {{docid?: string}} raw - Original Firestore doc (holds the docid).
 */

/**
 * @typedef {object} LinkTarget
 * @property {string} collection - Firestore collection to update.
 * @property {string} docid - Document that owns the array field.
 * @property {'Targetgroup'|'Activity'|'Effect'} field - Array field to change.
 * @property {string} valueId - Entity ID to add/remove from the array.
 */

// Keyed by `${from.step}->${to.step}`. `owner` picks which node holds the array;
// `value` picks which node's ID goes into it.
const RULES = {
  'doel->act': { collection: 'Activities', field: 'Targetgroup', owner: 'to', value: 'from' },
  'act->mech': { collection: 'TocMechanisms', field: 'Activity', owner: 'to', value: 'from' },
  'mech->eff': { collection: 'TocMechanisms', field: 'Effect', owner: 'from', value: 'to' },
};

/**
 * @param {NavNode|null|undefined} fromNode - Source card (where the drag started).
 * @param {NavNode|null|undefined} toNode - Target card (where it was dropped).
 * @returns {LinkTarget|null} The write to apply, or null when the pair is not a
 *   valid adjacent left→right link or the owner doc has no docid.
 */
const resolveLinkTarget = (fromNode, toNode) => {
  if (!fromNode || !toNode || !fromNode.raw || !toNode.raw) return null;
  if (fromNode === toNode || fromNode.id === toNode.id) return null;

  const rule = RULES[`${fromNode.step}->${toNode.step}`];
  if (!rule) return null;

  const nodes = { from: fromNode, to: toNode };
  const docid = nodes[rule.owner].raw.docid;
  if (!docid) return null;

  return {
    collection: rule.collection,
    docid,
    field: rule.field,
    valueId: nodes[rule.value].id,
  };
};

export default resolveLinkTarget;
